import { ReactNode, useState } from "react";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, AppRole } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, LogOut, Home, HelpCircle } from "lucide-react";
import { Footer } from "@/components/ui/footer";
import { OnboardingTutorial } from "@/components/OnboardingTutorial";
import { LanguageSelector } from "@/components/LanguageSelector";

interface PrivateLayoutProps {
  children: ReactNode;
}
export function PrivateLayout({
  children
}: PrivateLayoutProps) {
  const { signOut, isAuthenticated, initialized, getActiveRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [showTutorial, setShowTutorial] = useState(false);

  // Use centralized getActiveRole from useAuth
  const activeRole = getActiveRole();

  // Déterminer le label du rôle affiché
  const getUserRoleLabel = () => {
    if (activeRole === 'admin') return 'Administrateur';
    if (activeRole === 'prestataire') return 'Prestataire';
    if (activeRole === 'client') return 'Client';
    return null;
  };

  // Navigation vers le dashboard correspondant au rôle ACTIF
  const navigateToDashboard = () => {
    if (activeRole === 'admin') {
      navigate('/dashboard-admin');
    } else if (activeRole === 'client') {
      navigate('/dashboard-client');
    } else if (activeRole === 'prestataire') {
      navigate('/dashboard-prestataire');
    } else {
      navigate('/');
    }
  };
  return <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header pour pages privées - Fixed en haut */}
          <header className="fixed top-0 left-0 right-0 z-[100] h-14 flex items-center justify-between px-4 border-b border-border bg-background backdrop-blur-md shadow-sm">
            <div className="flex items-center space-x-2">
              <SidebarTrigger data-onboarding="sidebar-trigger" className="h-6 w-6" />
              
              {/* Bouton Dashboard/Accueil - juste après le sidebar trigger */}
              <Button
                data-onboarding="home-button"
                onClick={navigateToDashboard}
                variant="ghost"
                size="sm"
                className="flex items-center space-x-1 px-2"
                title="Retour au tableau de bord"
              >
                <Home className="h-5 w-5" />
                {!isMobile && <span>Accueil</span>}
              </Button>

              {/* Bouton Retour - à droite du bouton Accueil */}
              <Button 
                data-onboarding="back-button"
                onClick={() => navigate(-1)} 
                variant="ghost" 
                size="sm" 
                className="flex items-center space-x-1 px-2"
                title="Retour à la page précédente"
              >
                <ArrowLeft className="h-4 w-4" />
                {!isMobile && <span>Retour</span>}
              </Button>
              
              {/* Badge du rôle utilisateur */}
              {initialized && isAuthenticated() && getUserRoleLabel() && (
                <Badge data-onboarding="role-badge" variant="secondary">
                  {getUserRoleLabel()}
                </Badge>
              )}
            </div>

            {/* Boutons d'action à droite */}
            <div className="flex items-center space-x-2">
              {/* Bouton déconnexion */}
              <Button 
                data-onboarding="logout-button"
                onClick={async () => {
                  await signOut();
                  navigate('/');
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                }} 
                variant="destructive" 
                size="sm" 
                className="flex items-center space-x-2"
              >
                <LogOut className="h-4 w-4" />
                {!isMobile && <span>Déconnexion</span>}
              </Button>

              {/* Sélecteur de langue - entre déconnexion et aide */}
              <div data-onboarding="language-selector">
                <LanguageSelector />
              </div>

              {/* Bouton aide / tutoriel - complètement à droite */}
              <Button
                data-onboarding="help-button"
                onClick={() => setShowTutorial(true)}
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                title="Aide / Tutoriel"
              >
                <HelpCircle className="h-5 w-5 text-muted-foreground hover:text-foreground transition-colors" />
              </Button>
            </div>
          </header>

          {/* Tutoriel d'onboarding - adapté au rôle */}
          <OnboardingTutorial 
            forceShow={showTutorial} 
            onClose={() => setShowTutorial(false)}
            context={activeRole === 'admin' ? 'admin' : activeRole === 'prestataire' ? 'prestataire' : activeRole === 'client' ? 'client' : 'public'}
          />

          {/* Contenu principal - scroll natif de la page */}
          <main className="flex-1 w-full pt-14">
            {children}
          </main>

          {/* Footer visible sur toutes les pages */}
          <Footer />
        </div>
      </div>
    </SidebarProvider>;
}